import { startOfMonth } from "date-fns";
import { Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { APIError } from "@/adapters/api";
import { DatePicker } from "@/components/date-picker";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { usePrint } from "@/hooks/use-print.ts";
import {
	currentWorkDate,
	currentWorkDay,
	dateOnlyToDate,
	dateToDateOnly,
} from "@/lib/format.ts";
import { useMeasurementBulletin } from "../../hooks/use-measurement-bulletin.ts";
import type { Bulletin } from "../../types.ts";
import { BulletinPrint } from "../bulletin-print";

type PeriodField = "from" | "to";

const DESCRIPTION_MESSAGES: Record<string, string> = {
	required: "Campo obrigatório.",
	invalid: "Data inválida.",
	after_to: "A data inicial deve ser anterior ou igual à final.",
	future: "A data final não pode ser futura.",
};

function describe(description: string): string {
	return DESCRIPTION_MESSAGES[description] ?? description;
}

function defaultPeriod(): Record<PeriodField, string> {
	return {
		from: dateToDateOnly(startOfMonth(currentWorkDay())),
		to: currentWorkDate(),
	};
}

export function BulletinDialog({
	open,
	onOpenChange,
	constructionId,
	showMoney,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	showMoney: boolean;
}) {
	const [period, setPeriod] = useState(defaultPeriod);
	const [errors, setErrors] = useState<Partial<Record<PeriodField, string>>>(
		{},
	);
	const [formError, setFormError] = useState<string | null>(null);
	const [bulletin, setBulletin] = useState<Bulletin | null>(null);
	const fetchBulletin = useMeasurementBulletin(constructionId);
	const printer = usePrint();

	useEffect(() => {
		if (!open) return;
		setPeriod(defaultPeriod());
		setErrors({});
		setFormError(null);
	}, [open]);

	function setField(field: PeriodField, date: Date | undefined) {
		setPeriod((current) => ({ ...current, [field]: dateToDateOnly(date) }));
		setErrors((current) => ({ ...current, [field]: undefined }));
	}

	function applyAPIError(err: unknown) {
		const fallback = "Não foi possível gerar o boletim.";
		if (!(err instanceof APIError)) {
			setFormError(err instanceof Error ? err.message : fallback);
			return;
		}
		const next: Partial<Record<PeriodField, string>> = {};
		for (const [field, description] of Object.entries(err.violations)) {
			if (field === "from" || field === "to") {
				next[field] = describe(description);
			} else {
				setFormError(describe(description));
			}
		}
		setErrors(next);
		if (Object.keys(err.violations).length === 0) {
			setFormError(err.message || fallback);
		}
	}

	async function handleSubmit(event: FormEvent) {
		event.preventDefault();
		setFormError(null);
		const missing: Partial<Record<PeriodField, string>> = {};
		if (!period.from) missing.from = describe("required");
		if (!period.to) missing.to = describe("required");
		if (missing.from || missing.to) {
			setErrors(missing);
			return;
		}
		try {
			const result = await fetchBulletin.mutateAsync(period);
			setBulletin(result);
			onOpenChange(false);
			printer.print();
		} catch (err) {
			applyAPIError(err);
		}
	}

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent onOpenAutoFocus={(event) => event.preventDefault()}>
					<DialogHeader>
						<DialogTitle>Boletim de medição</DialogTitle>
						<DialogDescription>
							Escolha o período do boletim para imprimir.
						</DialogDescription>
					</DialogHeader>
					<form
						id="bulletin-form"
						className="bulletin-form"
						onSubmit={handleSubmit}
						noValidate
					>
						<div className="bulletin-form__fields">
							<div className="form-group">
								<Label htmlFor="bulletin-from">De</Label>
								<DatePicker
									id="bulletin-from"
									name="from"
									modal
									selected={dateOnlyToDate(period.from)}
									onSelected={(date) => setField("from", date)}
									aria-invalid={!!errors.from}
									calendarProps={{ disabled: { after: currentWorkDay() } }}
								/>
								{errors.from && <p className="form-error">{errors.from}</p>}
							</div>
							<div className="form-group">
								<Label htmlFor="bulletin-to">Até</Label>
								<DatePicker
									id="bulletin-to"
									name="to"
									modal
									selected={dateOnlyToDate(period.to)}
									onSelected={(date) => setField("to", date)}
									aria-invalid={!!errors.to}
									calendarProps={{ disabled: { after: currentWorkDay() } }}
								/>
								{errors.to && <p className="form-error">{errors.to}</p>}
							</div>
						</div>
						{formError && (
							<p role="alert" className="form-error-alert">
								{formError}
							</p>
						)}
					</form>
					<DialogFooter>
						<Button
							type="button"
							variant="ghost"
							onClick={() => onOpenChange(false)}
							disabled={fetchBulletin.isPending}
						>
							Cancelar
						</Button>
						<Button
							type="submit"
							form="bulletin-form"
							disabled={fetchBulletin.isPending}
						>
							{fetchBulletin.isPending && (
								<Loader2 className="bulletin-form__spinner" />
							)}
							Imprimir boletim
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{printer.printing && bulletin && (
				<BulletinPrint bulletin={bulletin} showMoney={showMoney} />
			)}
		</>
	);
}
